import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import AppLayout from '@component/layout/app-layout';
import ModuleRoute from '@component/routing/module-route';
import PageLoader from '@component/routing/page-loader';
import ProtectedRoute from '@component/routing/protected-route';
import {
  GAP_LOGIN_PATH,
  getDefaultPath,
  LOGIN_PATH,
  ROUTES,
} from '@constant/routes';
import { useAuth } from '@context/auth-context';
import type { ModuleId } from '@type/module';

const LoginPage = lazy(() => import('@feature/auth/pages/login-page'));
const DashboardPage = lazy(() => import('@feature/dashboard/pages/dashboard-page'));
const VisualPage = lazy(() => import('@feature/visual/pages/visual-page'));
const CoesPage = lazy(() => import('@feature/coes/pages/coes-page'));
const ProcurementPage = lazy(() => import('@feature/procurement/pages/procurement-page'));
const ContractMgmtPage = lazy(() => import('@feature/contract-mgmt/pages/contract-mgmt-page'));
const TrainingPage = lazy(() => import('@feature/training/pages/training-page'));
const GapPage = lazy(() => import('@feature/gap/pages/gap-page'));
const AccountsPage = lazy(() => import('@feature/accounts/pages/accounts-page'));
const BudgetPage = lazy(() => import('@feature/budget/pages/budget-page'));

function withSuspense(element: ReactNode) {
  return <Suspense fallback={<PageLoader />}>{element}</Suspense>;
}

function LoginRoute() {
  const { isAuthenticated, user } = useAuth();
  if (isAuthenticated) {
    return <Navigate to={getDefaultPath(user)} replace />;
  }
  return withSuspense(<LoginPage />);
}

function GapLoginRoute() {
  const { isAuthenticated, user } = useAuth();
  if (isAuthenticated && user?.modules.includes('gap')) {
    return <Navigate to={ROUTES.gap} replace />;
  }
  if (isAuthenticated) {
    return <Navigate to={getDefaultPath(user)} replace />;
  }
  return withSuspense(<LoginPage variant="gap" />);
}

function RootRedirect() {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to={LOGIN_PATH} replace />;
  return <Navigate to={getDefaultPath(user)} replace />;
}

function FallbackRedirect() {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to={LOGIN_PATH} replace />;
  return <Navigate to={getDefaultPath(user)} replace />;
}

function modulePage(module: ModuleId, Page: ComponentType) {
  return (
    <ModuleRoute module={module}>
      {withSuspense(<Page />)}
    </ModuleRoute>
  );
}

export const router = createBrowserRouter([
  {
    path: ROUTES.login,
    element: <LoginRoute />,
  },
  {
    path: GAP_LOGIN_PATH,
    element: <GapLoginRoute />,
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            path: ROUTES.dashboard,
            element: modulePage('dashboard', DashboardPage),
          },
          {
            path: ROUTES.visual,
            element: modulePage('visual', VisualPage),
          },
          {
            path: ROUTES.coes,
            element: modulePage('coes', CoesPage),
          },
          {
            path: ROUTES.procurement,
            element: modulePage('procurement', ProcurementPage),
          },
          {
            path: ROUTES.contractMgmt,
            element: modulePage('contractmgmt', ContractMgmtPage),
          },
          {
            path: ROUTES.training,
            element: modulePage('training', TrainingPage),
          },
          {
            path: ROUTES.gap,
            element: modulePage('gap', GapPage),
          },
          {
            path: ROUTES.accounts,
            element: modulePage('accounts', AccountsPage),
          },
          {
            path: ROUTES.budget,
            element: modulePage('budget', BudgetPage),
          },
        ],
      },
    ],
  },
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    path: '*',
    element: <FallbackRedirect />,
  },
]);
